import { CAST_AI } from '@cast/constants';
import { Icons, focusProps, Stack } from '@cast/design-system';

import {
  OpDemoWoopImage,
  OpDemoEvictorImage,
  OpDemoOptimizationImage,
  OpDemoNodesAutoscalerImage,
  OpDemoNodesImage,
  OpDemoRebalancerImage,
  OpDemoWorkloadsImage,
} from 'assets/svg';
import {
  onClickTrigger,
  button,
  withText,
  element,
  insideElementWithTestId,
} from 'core/tour-v2/event-matchers';
import { PageTourArgs } from 'core/tour-v2/types';
import { BlurredElement } from 'features/demo/_components/overlay/BlurredElement';
import { BlurredSidebar } from 'features/demo/_components/overlay/BlurredSidebar';
import { TourPages, TourSteps, TourPageToState } from 'features/demo/types';

import { NavigationBlocker } from './_components/overlay/NavigationBlocker';
import {
  TourStepContainer,
  TourStepTitle,
  TourStepText,
  TourStepButton,
  TourStepImage,
  TourStepToolbar,
} from './_components/tooltip';
import {
  getAutoscalerDemoData1,
  getAutoscalerDemoData2,
  getAutoscalerDemoData3,
  getAutoscalerDemoData4,
  getAutoscalerDemoData5,
} from './data/autoscaler-settings';

const settingsSections = {
  woop: 'woop-autoscaling-section',
  node: 'node-autoscaling-section',
  evictor: 'evictor-section',
  podMutator: 'pod-mutator-section',
} as const;

type SettingSection = keyof typeof settingsSections;

const settingSectionSelector = (section: SettingSection) =>
  `[data-testid=${settingsSections[section]}]`;

const settingSwitchSelector = (
  section: Exclude<SettingSection, 'podMutator'>
) => `${settingSectionSelector(section)} [role=switch]`;

const focusSettingSwitch = (
  section: Exclude<SettingSection, 'podMutator'>
) => ({
  [`${settingSwitchSelector(section)} .DS-Switch-thumb`]: focusProps(),
});

const blurSettingsExcept = (section: SettingSection) => ({
  [`.DsPaper-Root:not([data-testid="${settingsSections[section]}"])`]: {
    filter: 'blur(1.5px)',
    pointerEvents: 'none',
    userSelect: 'none',
  },
});

const indigoHighlight = (selector: string) => ({
  [selector]: {
    outline: '1px solid',
    outlineColor: 'var(--indigo-300)',
    boxShadow: '0 0 4px 0 var(--indigo-200)',
  },
});

const onSidebarItemClick = (item: string) =>
  onClickTrigger(element, [insideElementWithTestId(`sidebar-item-${item}`)]);

export const pageTourSteps = [
  {
    id: TourPages.Dashboard,
    url: '/automation/cluster-list',
    steps: [
      {
        id: TourSteps.Initial,
        goNext: onClickTrigger(button, [withText('Run simulation')]),
        tooltip: {
          anchor: { testId: 'demo-btn' },
          placement: 'left-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer pointTo={[{ testId: 'demo-btn' }]}>
              <TourStepTitle>Experience {CAST_AI}’s Impact</TourStepTitle>
              <TourStepText>
                Run a realistic simulation to watch {CAST_AI} reduce waste,
                improve efficiency, and lower costs.
              </TourStepText>
              <TourStepImage>
                <OpDemoOptimizationImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
      },
    ],
  },
  {
    id: TourPages.AutoscalerSettings,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/settings',
    steps: [
      {
        id: TourSteps.EnableWoop,
        state: getAutoscalerDemoData1,
        globalStyles: {
          ...focusSettingSwitch('woop'),
          ...blurSettingsExcept('woop'),
        },
        tooltip: {
          anchor: {
            selector: settingSwitchSelector('woop'),
          },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[
                {
                  selector: settingSwitchSelector('woop'),
                },
              ]}
            >
              <TourStepToolbar noBack />
              <TourStepTitle>Workload autoscaling</TourStepTitle>
              <TourStepText>
                Workload Autoscaler adjusts CPU and memory requests for
                workloads based on their actual usage patterns. It continuously
                learns from real demand to prevent waste and improve
                performance. Enable it to start optimizing your workloads
                automatically.
              </TourStepText>
              <TourStepImage>
                <OpDemoWoopImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
      },
      {
        id: TourSteps.GoToWoop,
        goNext: onSidebarItemClick('woop-dashboard'),
        state: getAutoscalerDemoData2,
        globalStyles: {
          ...indigoHighlight('[data-testid=sidebar-item-woop-dashboard]'),
          ...blurSettingsExcept('woop'),
        },
        tooltip: {
          anchor: { testId: 'sidebar-item-woop-dashboard' },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[{ testId: 'sidebar-item-woop-dashboard' }]}
            >
              <TourStepToolbar />
              <TourStepTitle>Observe optimization</TourStepTitle>
              <TourStepText>
                You started workload optimization! Head to Workloads page to see
                how it is applied
              </TourStepText>
              <TourStepText italic>
                The utilization chart at the top will reflect these changes as
                the demo progresses.
              </TourStepText>
              <TourStepImage>
                <OpDemoWorkloadsImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar skippedItems={['sidebar-item-woop-dashboard']} />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.WoopList,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/workload-optimization/workloads?woop-list_sortBy=podCount-vertical%3Adesc%3ApodCount',
    steps: [
      {
        id: TourSteps.UnoptimizedWorkloads,
        tooltip: {
          anchor: {
            selector: '.DS-Table-HeaderCellWrapper.DS-Table-CellWrapper-status',
          },
          placement: 'left-start',
          continuouslyWatch: true,
          distance: 200,
          content: (api) => (
            <TourStepContainer
              pointTo={[
                {
                  selector:
                    '.DS-Table-HeaderCellWrapper.DS-Table-CellWrapper-status',
                },
              ]}
            >
              <TourStepToolbar />
              <TourStepTitle>Workload optimization process</TourStepTitle>
              <TourStepText>
                {CAST_AI} analyzes your workloads to ensure accurate
                optimizations. Once ready, resource recommendations are applied
                automatically to improve efficiency.
              </TourStepText>
              <TourStepButton onClick={api.next}>
                <Stack direction="row" alignItems="center" gap={8}>
                  Fast-forward to see impact
                  <Icons.ArrowRight size={20} />
                </Stack>
              </TourStepButton>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
            <BlurredElement
              selector={{ testId: 'optimize-using-policies-button' }}
            />
            <BlurredElement selector={{ testId: 'other-features-card' }} />
            <BlurredElement
              selector={{ testId: 'workload-list-actions-select' }}
            />
          </>
        ),
      },
      {
        id: TourSteps.GoToAutoscalerSettings,
        tooltip: {
          anchor: { testId: 'event-marker-recommendations-started-applying' },
          placement: 'left-start',
          continuouslyWatch: true,
          distance: 0,
          content: (api) => (
            <TourStepContainer
              pointTo={[
                { testId: 'event-marker-recommendations-started-applying' },
              ]}
            >
              <TourStepToolbar />
              <TourStepTitle>Impact on the CPU requests</TourStepTitle>
              <TourStepText>
                Workload Autoscaler has rightsized CPU requests based on actual
                usage. Notice how provisioned CPU remains high while requests
                have decreased. This gap shows an opportunity for node-level
                optimization.
              </TourStepText>
              <TourStepButton onClick={api.next}>
                <Stack direction="row" alignItems="center" gap={8}>
                  Go to Autoscaler settings
                  <Icons.ArrowRight size={20} />
                </Stack>
              </TourStepButton>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
            <BlurredElement
              selector={{ testId: 'optimize-using-policies-button' }}
            />
            <BlurredElement selector={{ testId: 'other-features-card' }} />
            <BlurredElement
              selector={{ testId: 'workload-list-actions-select' }}
            />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.AutoscalerSettings,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/settings',
    steps: [
      {
        id: TourSteps.EnableEvictor,
        state: getAutoscalerDemoData3,
        globalStyles: {
          ...focusSettingSwitch('evictor'),
          ...blurSettingsExcept('evictor'),
        },
        tooltip: {
          anchor: {
            selector: settingSwitchSelector('evictor'),
          },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[
                {
                  selector: settingSwitchSelector('evictor'),
                },
              ]}
            >
              <TourStepToolbar />
              <TourStepTitle>Enable evictor</TourStepTitle>
              <TourStepText>
                Now that workloads use less CPU, Evictor can consolidate pods
                onto fewer nodes. This frees up underutilized nodes that can be
                removed, closing the gap between provisioned and requested
                resources.
              </TourStepText>
              <TourStepImage>
                <OpDemoEvictorImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
      },
      {
        id: TourSteps.GoToNodesAfterEnablingEvictor,
        goNext: onSidebarItemClick('node-list'),
        state: getAutoscalerDemoData4,
        globalStyles: {
          ...indigoHighlight('[data-testid=sidebar-item-node-list]'),
          ...blurSettingsExcept('evictor'),
        },
        tooltip: {
          anchor: { testId: 'sidebar-item-node-list' },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer pointTo={[{ testId: 'sidebar-item-node-list' }]}>
              <TourStepToolbar />
              <TourStepTitle>Observe nodes</TourStepTitle>
              <TourStepText>
                Navigate to the Nodes list to see how Evictor has consolidated
                workloads. You&apos;ll notice fewer active nodes with higher
                resource utilization.
              </TourStepText>
              <TourStepImage>
                <OpDemoNodesImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar skippedItems={['sidebar-item-node-list']} />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.NodeList,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/nodes',
    steps: [
      {
        id: TourSteps.ObserveEvictorOnNodes,
        tooltip: {
          anchor: {
            xpath: '//div[@id="page-title"]//h3[contains(text(), "Node list")]',
          },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (api) => (
            <TourStepContainer
              pointTo={[
                {
                  xpath:
                    "//div[contains(@class, 'DS-Table-GroupHeader') and text()='Resource Requests']",
                },
                { testId: 'event-marker-recommendations-started-applying' },
              ]}
            >
              <TourStepToolbar />
              <TourStepTitle>Ready to remove nodes</TourStepTitle>
              <TourStepText>
                Evictor has consolidated workloads onto fewer nodes, leaving
                several with minimal utilization.
              </TourStepText>
              <TourStepText>
                Node autoscaling can remove these underutilized nodes to
                eliminate the remaining gap between provisioned and requested
                resources.
              </TourStepText>
              <TourStepButton onClick={api.next}>
                <Stack direction="row" alignItems="center" gap={8}>
                  Go to Autoscaler settings
                  <Icons.ArrowRight size={20} />
                </Stack>
              </TourStepButton>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.AutoscalerSettings,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/settings',
    steps: [
      {
        id: TourSteps.EnableNodeAutoscaler,
        state: getAutoscalerDemoData4,
        globalStyles: {
          ...focusSettingSwitch('node'),
          ...blurSettingsExcept('node'),
        },
        tooltip: {
          anchor: {
            selector: settingSwitchSelector('node'),
          },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[
                {
                  selector: settingSwitchSelector('node'),
                },
              ]}
            >
              <TourStepToolbar />
              <TourStepTitle>Enable node autoscaling</TourStepTitle>
              <TourStepText>
                Enable node autoscaling to remove underutilized nodes
                automatically, finishing what Evictor started by eliminating
                excess capacity.
              </TourStepText>
              <TourStepImage>
                <OpDemoNodesAutoscalerImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
      },
      {
        id: TourSteps.GoToNodesAfterEnablingNodeAutoscaler,
        goNext: onSidebarItemClick('node-list'),
        state: getAutoscalerDemoData5,
        globalStyles: {
          ...indigoHighlight('[data-testid=sidebar-item-node-list]'),
          ...blurSettingsExcept('node'),
        },
        tooltip: {
          anchor: { testId: 'sidebar-item-node-list' },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer pointTo={[{ testId: 'sidebar-item-node-list' }]}>
              <TourStepToolbar />
              <TourStepTitle>See node autoscaling in action</TourStepTitle>
              <TourStepText>
                Node autoscaling has removed the underutilized nodes identified
                earlier. Notice how the provisioned CPUs have dropped to match
                more closely with resource requests, eliminating waste.
              </TourStepText>
              <TourStepImage>
                <OpDemoNodesImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar skippedItems={['sidebar-item-node-list']} />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.NodeList,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/nodes',
    steps: [
      {
        id: TourSteps.GoToRebalancer,
        goNext: onSidebarItemClick('rebalancer'),
        globalStyles: indigoHighlight('[data-testid=sidebar-item-rebalancer]'),
        tooltip: {
          anchor: { testId: 'sidebar-item-rebalancer' },
          placement: 'right-start',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[{ testId: 'sidebar-item-rebalancer' }]}
            >
              <TourStepToolbar />
              <TourStepTitle>Infrastructure rebalancing</TourStepTitle>
              <TourStepText>
                With workloads rightsized and consolidated, rebalancing takes
                the final step by replacing existing infrastructure with more
                cost-effective instance types.
              </TourStepText>
              <TourStepImage>
                <OpDemoRebalancerImage />
              </TourStepImage>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar skippedItems={['sidebar-item-rebalancer']} />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.Rebalancer,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/rebalance/d6a87a19-51c8-4934-8c93-192a3c3d00cb/plan',
    steps: [
      {
        id: TourSteps.RebalanceNow,
        goNext: onClickTrigger(button, [withText('Rebalance')]),
        tooltip: {
          anchor: { testId: 'footer-rebalance-button' },
          placement: 'left-end',
          continuouslyWatch: true,
          distance: 88,
          content: (
            <TourStepContainer
              pointTo={[{ testId: 'footer-rebalance-button' }]}
            >
              <TourStepToolbar />
              <TourStepTitle>New configuration review</TourStepTitle>
              <TourStepText>
                Compare your current infrastructure with our optimized
                configuration. Start rebalancing to consolidate workloads onto
                more cost-effective instance types.
              </TourStepText>
            </TourStepContainer>
          ),
        },
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
            <BlurredElement selector={{ testId: 'footer-back-button' }} />
          </>
        ),
      },
    ],
  },
  {
    id: TourPages.RebalancingProgress,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/rebalance/d6a87a19-51c8-4934-8c93-192a3c3d00cb/progress',
    steps: [
      {
        id: TourSteps.RebalanceProgress,
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
        // No goNext - tour advances via api.next() in RebalanceProgress.tsx when progress reaches 100%
      },
    ],
  },
  {
    id: TourPages.RebalancingCompleted,
    url: '/automation/external-clusters/ca0cdb62-7e1b-45f3-94bf-fe0f8b187954/rebalance/d6a87a19-51c8-4934-8c93-192a3c3d00cb/completed',
    steps: [
      {
        id: TourSteps.RebalancingFinished,
        overlay: (
          <>
            <NavigationBlocker />
            <BlurredSidebar />
          </>
        ),
        goNext: onClickTrigger(button, [withText('Done')]),
      },
    ],
  },
  {
    id: TourPages.Dashboard,
    url: '/automation/cluster-list',
    steps: [
      {
        id: TourSteps.ShowDemoResults,
      },
    ],
  },
] as const satisfies PageTourArgs<TourPageToState>[];
